/**
 * WHICH DOGS A CARE FLAG COVERS, on one stay (per-dog-care-flags).
 *
 * `bookings.food_at_house`, `bookings.meds` and `bookings.mix_ok` are one tick
 * each for the whole booking, so a household with three dogs put "Medication"
 * on the 7am staff brief and named nobody — true, and useless, which is the
 * worst shape a warning can take.
 *
 * Pure, so the component only draws and every rule below is a unit test. The
 * standing discipline in this folder, and the reason `flags.ts` and `domain.ts`
 * look the way they do.
 */

/** The three flags that became per-dog. */
export type CareKind = 'food' | 'meds' | 'mix';

/** What the seeding rules read off a dog. Structural, so `OwnerDog` satisfies it. */
export interface SeedableDog {
  id: string;
  meds?: string | null;
  keepApart?: boolean | null;
}

function hasText(v: string | null | undefined): v is string {
  return typeof v === 'string' && v.trim() !== '';
}

/**
 * Who is already ticked the moment the tile goes on.
 *
 * WE ALREADY KNOW MOST OF THIS. `dogs.meds` says who takes something and
 * `dogs.keep_apart` is the household's own answer from registration, so
 * pre-ticking them is showing our work rather than guessing. Food has no
 * household record anywhere, and one household's food for one household's dogs
 * is the case that walks in the door — so everybody, and the desk unticks the
 * exception.
 *
 * ONE DOG IS ALWAYS THE ANSWER. Asking *which* of one is a question with a
 * single possible reply, so the seed does not consult a record that could leave
 * the tile on for nobody.
 *
 * AND A SEED OF NOBODY FALLS BACK TO EVERYBODY. Ticking the tile means *this
 * applies*; with nothing better to go on it applies to all of them. An empty
 * seed would turn the tile straight back off (see `toggleDog`), which would
 * make it untickable on exactly the households that most need it.
 */
export function seedSelection(kind: CareKind, dogs: SeedableDog[]): string[] {
  if (dogs.length === 0) return [];
  if (dogs.length === 1) return [dogs[0]!.id];
  const known =
    kind === 'meds' ? dogs.filter((d) => hasText(d.meds))
    : kind === 'mix' ? dogs.filter((d) => d.keepApart === true)
    : dogs;
  return (known.length ? known : dogs).map((d) => d.id);
}

/** What a booking's own rows say about one dog, per flag (0034). Null = nobody asked. */
export interface SelectedDog {
  id: string;
  foodAtHouse?: boolean | null;
  needsMeds?: boolean | null;
  keepApart?: boolean | null;
}

const FIELD: Record<CareKind, keyof Omit<SelectedDog, 'id'>> = { food: 'foodAtHouse', meds: 'needsMeds', mix: 'keepApart' };

/**
 * What the pane OPENS with.
 *
 * A TILE ON FOR NOBODY IS THE STATE THIS FEATURE EXISTS TO END, and two
 * openings produced it: an existing booking carrying the legacy tick with
 * nulls across its join, and a new booking whose tile the household flag
 * seeded. Both drew the tile pressed and the list unticked, and a Save sent
 * the warning on with no subject.
 *
 * So: the booking's own rows where ANY dog has answered (AC20); otherwise —
 * nobody asked, or there are no rows yet — the same seed the first tick uses,
 * which is how an old row gains names the first time somebody opens and saves
 * it (0034). Off is off, whatever the rows say.
 */
export function initialSelection(
  kind: CareKind,
  on: boolean,
  rows: SelectedDog[] | null | undefined,
  dogs: SeedableDog[],
): string[] {
  if (!on) return [];
  const field = FIELD[kind];
  const answered = (rows ?? []).some((r) => r[field] != null);
  if (answered) {
    const picked = new Set((rows ?? []).filter((r) => r[field] === true).map((r) => r.id));
    return dogs.filter((d) => picked.has(d.id)).map((d) => d.id);
  }
  return seedSelection(kind, dogs);
}

/**
 * Tick or untick one dog.
 *
 * `order` keeps the result in the HOUSEHOLD's own order rather than the order
 * they were clicked — otherwise the list reshuffles under the hand that is
 * clicking it, which is the kind of thing nobody reports and everybody
 * distrusts.
 *
 * An empty result is a real answer and the caller acts on it: a flag on for
 * nobody is what reaches the brief saying "Food at house" with no food and no
 * dog behind it, so the tile turns itself off.
 */
export function toggleDog(selected: string[], dogId: string, order?: string[]): string[] {
  const next = selected.includes(dogId)
    ? selected.filter((id) => id !== dogId)
    : [...selected, dogId];
  if (!order) return next;
  return order.filter((id) => next.includes(id));
}
